/**
 * Fully tear down WebRTC / HTML media so Chrome Android clears the
 * "Phone speaker" / Live notification after the patient call ends.
 */

const stopStream = (stream) => {
  if (!stream) return;
  try {
    if (typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_e) {
          /* ignore */
        }
      });
    }
  } catch (_e) {
    /* ignore */
  }
  try {
    if (typeof stream.stop === 'function') {
      stream.stop();
    }
  } catch (_e) {
    /* ignore */
  }
};

const silenceMediaElement = (el) => {
  if (!el) return;
  try {
    el.pause();
  } catch (_e) {
    /* ignore */
  }
  try {
    el.muted = true;
  } catch (_e) {
    /* ignore */
  }
  try {
    el.srcObject = null;
  } catch (_e) {
    /* ignore */
  }
  try {
    el.removeAttribute('src');
  } catch (_e) {
    /* ignore */
  }
  try {
    if (typeof el.load === 'function') {
      el.load();
    }
  } catch (_e) {
    /* ignore */
  }
};

export const clearHtmlCallMedia = (root = document) => {
  try {
    const nodes = root.querySelectorAll(
      '#videos-container video, #videos-container audio, #video, #canvasVideo, video, audio'
    );
    nodes.forEach((el) => {
      // Only clear call tiles / ring elements; avoid nuking unrelated page audio if any.
      const id = el.id || '';
      const inCallContainer = !!(el.closest && el.closest('#videos-container'));
      if (inCallContainer || id === 'video' || id === 'canvasVideo') {
        silenceMediaElement(el);
      }
    });
  } catch (_e) {
    /* ignore */
  }
  try {
    root.querySelectorAll('#videos-container .media-container').forEach((node) => {
      try {
        node.remove();
      } catch (_e) {
        /* ignore */
      }
    });
  } catch (_e) {
    /* ignore */
  }
};

export const clearMediaSession = () => {
  try {
    if (!navigator.mediaSession) return;
    navigator.mediaSession.playbackState = 'none';
    navigator.mediaSession.metadata = null;
    [
      'play',
      'pause',
      'stop',
      'seekbackward',
      'seekforward',
      'previoustrack',
      'nexttrack',
    ].forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, null);
      } catch (_e) {
        /* ignore */
      }
    });
  } catch (_e) {
    /* ignore */
  }
};

export const disposeHtmlAudio = (audio) => {
  if (!audio) return;
  try {
    audio.pause();
  } catch (_e) {
    /* ignore */
  }
  try {
    audio.currentTime = 0;
  } catch (_e) {
    /* ignore */
  }
  try {
    audio.removeAttribute('src');
    audio.load();
  } catch (_e) {
    /* ignore */
  }
};

/**
 * @param {object} options
 * @param {MediaStream|null} options.mediaStream
 * @param {object|null} options.connection RTCMultiConnection instance
 * @param {HTMLAudioElement|null} [options.ringAudio]
 * @param {Function} [options.stopRingTone]
 * @param {Function} [options.stopAckSpeech]
 */
export const releaseWebRtcCallMedia = ({
  mediaStream = null,
  connection = null,
  ringAudio = null,
  stopRingTone = null,
  stopAckSpeech = null,
} = {}) => {
  try {
    if (typeof stopRingTone === 'function') {
      stopRingTone();
    }
  } catch (_e) {
    /* ignore */
  }
  try {
    if (typeof stopAckSpeech === 'function') {
      stopAckSpeech(true);
    }
  } catch (_e) {
    /* ignore */
  }

  disposeHtmlAudio(ringAudio);
  clearHtmlCallMedia(document);

  stopStream(mediaStream);

  try {
    (connection?.attachStreams || []).forEach(stopStream);
  } catch (_e) {
    /* ignore */
  }
  try {
    Object.values(connection?.streamEvents || {}).forEach((event) => {
      stopStream(event?.stream);
    });
  } catch (_e) {
    /* ignore */
  }

  try {
    const participants = connection?.getAllParticipants?.() || [];
    participants.forEach((pid) => {
      try {
        connection.deletePeer?.(pid);
      } catch (_e) {
        /* ignore */
      }
    });
  } catch (_e) {
    /* ignore */
  }

  try {
    connection?.close?.();
  } catch (_e) {
    /* ignore */
  }
  try {
    connection?.closeSocket?.();
  } catch (_e) {
    /* ignore */
  }

  clearMediaSession();
};

export default releaseWebRtcCallMedia;
